import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
} from '../../../components/mui/Core/index'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import RoleManagementTable from './components/RoleManagementTable/RoleManagementTable'
import DeleteRoleModal from './components/DeleteRoleModal/DeleteRoleModal'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import GenericInfoModal from '../../../components/GenericModal/GenericInfoModal'
import TableSearch from '../../../components/TableSearch/table-search'
import { fetchRoleApplication } from './components/PermissionsList/permissions-list-slice'
import { useApplicationList } from '../../../hooks/applications/application-list'
import { fetchRoles } from './view-role-slice'
import { deleteRole } from './components/UpdateRole/update-role-slice'

function RoleManagement() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isFetchingRoles = useSelector((state) => state.roleManagement.viewRole.isFetchingRoles)
  const isUpdatingRole = useSelector((state) => state.roleManagement.updateRole.isUpdatingRole)
  const applicationList = useApplicationList()
  const isLoading = isUpdatingRole || isFetchingRoles
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false)
  const [isErrorDeleteModalOpen, setIsErrorDeleteModalOpen] = useState(false)
  const [selectedRoleToDelete, setSelectedRoleToDelete] = useState(null)

  useEffect(() => {
    dispatch(fetchRoles())
  }, [dispatch, isUpdatingRole])

  useEffect(() => {
    if (!applicationList || Object.keys(applicationList).length === 0) {
      dispatch(fetchRoleApplication())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const searchByOptions = [
    { id: 'code', text: 'Role Code', type: 'string' },
    { id: 'description', text: 'Role Description', type: 'string' },
    { id: 'application', text: 'Application', type: 'string' },
    { id: 'modifiedBy', text: 'Modified By', type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const handleDeleteRoleModalClose = () => {
    setSelectedRoleToDelete(null)
    setIsDeleteRoleModalOpen(false)
  }
  const handleDeleteRoleModalOpen = (role) => {
    setIsDeleteRoleModalOpen(true)
    setSelectedRoleToDelete({ ...role })
  }
  const handleConfirmDeleteRole = async () => {
    if (!selectedRoleToDelete) return

    const role = {
      roleName: selectedRoleToDelete?.code,
      roleDescription: selectedRoleToDelete?.description,
    }

    try {
      setIsDeleteRoleModalOpen(false)
      await dispatch(
        deleteRole({
          role,
          application: selectedRoleToDelete?.application,
        }),
      ).unwrap()

      setSelectedRoleToDelete(null)

      dispatch(
        setSnackbar({
          message: Constants.roleManagement.deleteRole.SUCCESS_DELETE_ROLE.replace(
            '__ROLE_CODE__',
            selectedRoleToDelete?.code,
          ),
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    } catch (error) {
      setIsDeleteRoleModalOpen(false)

      if (error?.status === 409) {
        setSelectedRoleToDelete((prev) => ({
          ...prev,
          apiErrorMessage: error?.message,
        }))
        setIsErrorDeleteModalOpen(true)
        return
      }
      setSelectedRoleToDelete(null)

      dispatch(
        setSnackbar({
          message: Constants.roleManagement.deleteRole.ERROR_DELETE_ROLE.replace(
            '__ROLE_CODE__',
            selectedRoleToDelete?.code,
          ),
          severity: 'error',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
  }

  const handleErrorDeleteModalClose = () => {
    setIsErrorDeleteModalOpen(false)
    setSelectedRoleToDelete(null)
  }

  const handleCreateRole = () => {
    navigate('/role-management/create-role')
  }
  const handleManagePermissions = () => {
    navigate('/role-management/manage-permissions')
  }

  const buttonStackStyle = {
    marginTop: 2,
    marginBottom: 4,
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <PageTitle title={Constants.roleManagement.ROLE_MANAGEMENT} />
          </Grid>
          <Grid
            size={{ xs: 12 }}
            container
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid display='flex' justifyContent='left' alignItems='flex-end'>
              <Stack spacing={4} direction='row' container='true' sx={buttonStackStyle}>
                <Button variant='contained' color='secondary' onClick={handleCreateRole}>
                  {Constants.roleManagement.CREATE_ROLE}
                </Button>
                <Button color='secondary' variant='outlined' onClick={handleManagePermissions}>
                  {Constants.roleManagement.MANAGE_PERMISSIONS}
                </Button>
              </Stack>
            </Grid>
            <Grid>
              <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <RoleManagementTable
              filterBy={tableFilterBy}
              filterValue={tableFilterValue}
              searchByOptions={searchByOptions}
              handleDeleteRoleModalOpen={handleDeleteRoleModalOpen}
            />
          </Grid>
        </Grid>
      </Container>

      <DeleteRoleModal
        isOpen={isDeleteRoleModalOpen}
        selectedRoleToDelete={selectedRoleToDelete}
        handleCloseAction={handleDeleteRoleModalClose}
        onConfirm={handleConfirmDeleteRole}
      />
      <GenericInfoModal
        isOpen={isErrorDeleteModalOpen}
        title={Constants.roleManagement.deleteRole.ERROR_DELETE_ROLE_TITLE}
        content={
          selectedRoleToDelete?.apiErrorMessage ||
          Constants.roleManagement.deleteRole.ERROR_DELETE_ROLE_MESSAGE
        }
        subContent={selectedRoleToDelete?.code}
        buttonText={Constants.roleManagement.CLOSE}
        handleClose={handleErrorDeleteModalClose}
      />
    </Box>
  )
}

export default RoleManagement
